import type { Entity as WorkbookEntity } from '~/@entities/(Content)/Workbook/dto'
import type { DeliveryCompany } from '~/@entities/Delivery/type'
import { CoverData } from '~/@entities/DesignTemplate/CoverData/dto'
import type { Entity as ProductDetailEntity } from '~/@entities/PODMall/ProductDetail/dto'
import type { PrintDocumentCoverDataT } from '~/@entities/PrintDocument/type'

import type { Entity as CartItemEntity } from '../CartItem/dto'
import type { PodPaymentPayerType } from '../Payment/type'
import type { PdfType, PrintType, ProductType } from '../Product/type'
import type { PODApi } from './api'
import type { OrderStatusType } from './type'

export namespace Entity {
  export class DirectOrder {
    id: string
    totalQuantity: number
    totalAmount: number
    productAmount: number
    deliveryAmount: number
    discountAmount: number
    paymentAmount: number | null

    additionalAmount: number
    productDiscountAmount: number
    productDiscountEventName: string | null
    productDiscountRate: number

    productName: string
    productThumbnailImageUrl: string
    name: string
    phoneNumber: number
    email: string | null
    address: string
    addressDetail: string
    zipCode: string

    /*
      PAYMENT_COMPLETED: 결제 완료
      IN_PRODUCTION: 제작 중
      PRODUCTION_COMPLETED: 제작 완료
      IN_DELIVERY: 배송 중
      DELIVERY_COMPLETED: 배송 완료
      CANCELED_BY_USER: 유저의 주문 취소
      CANCELED_BY_ADMIN: 관리자의 주문 취소
    */
    status: OrderStatusType
    orderDatetime: Date
    paymentCompany: 'TOSS_PAYMENT'
    paymentKey: string
    deliveryCompany: DeliveryCompany | null
    deliveryTrackingNumber: string | null
    orderItemProductTypes: ProductType[]

    repProductName: string
    repProductThumbnailImageUrl: string

    deliveryRecipientName: string
    deliveryRecipientStudentNames?: string[]
    deliveryRecipientPhoneNumber: string
    deliveryRecipientEmail: string
    deliveryAddress: string
    deliveryAddressDetail: string
    deliveryMemo: string
    deliveryZipCode: string | null
    paymentMethod: string
    pointDiscountAmount: number

    couponName: string | null
    userCouponId: number | null

    paymentType: PodPaymentPayerType

    constructor(props: PODApi.Response.DirectOrder) {
      this.id = props.id
      this.totalQuantity = props.totalQuantity
      this.totalAmount = props.totalAmount
      this.productAmount = props.productAmount
      this.deliveryAmount = props.deliveryAmount
      this.discountAmount = props.discountAmount
      this.paymentAmount = props.paymentAmount
      this.status = props.status
      this.orderDatetime = new Date(props.orderDatetime)
      this.paymentCompany = props.paymentCompany
      this.paymentKey = props.paymentKey
      this.deliveryCompany = props.deliveryCompany
      this.deliveryTrackingNumber = props.deliveryTrackingNumber
      this.orderItemProductTypes = props.orderItemProductTypes
      this.repProductName = props.repProductName
      this.repProductThumbnailImageUrl = props.repProductThumbnailImageUrl
      this.deliveryRecipientName = props.deliveryRecipientName
      this.deliveryRecipientStudentNames = props.deliveryRecipientStudentNames
      this.deliveryRecipientPhoneNumber = props.deliveryRecipientPhoneNumber
      this.deliveryRecipientEmail = props.deliveryRecipientEmail
      this.deliveryAddress = props.deliveryAddress
      this.deliveryAddressDetail = props.deliveryAddressDetail
      this.deliveryMemo = props.deliveryMemo
      this.deliveryZipCode = props.deliveryZipCode
      this.paymentMethod = props.paymentMethod
      this.couponName = props.couponName
      this.userCouponId = props.userCouponId
      this.paymentType = props.paymentType

      this.additionalAmount = props.additionalAmount
      this.productDiscountAmount = props.productDiscountAmount
      this.productDiscountEventName = props.productDiscountEventName
      this.productDiscountRate = props.productDiscountRate
      this.productName = props.productName
      this.productThumbnailImageUrl = props.productThumbnailImageUrl
      this.pointDiscountAmount = props.pointDiscountAmount

      this.name = props.name
      this.phoneNumber = props.phoneNumber
      this.email = props.email
      this.address = props.address
      this.addressDetail = props.addressDetail
      this.zipCode = props.zipCode
    }
  }

  export class ParentOrder {
    groupOrderId: string
    productList: {
      productThumbnailImageUrl: string
      productName: string
      orderItemProductTypes: ProductType[]
      orderDatetime: Date
      totalQuantity: number
      salesPrice: number
      additionalPrice: number
    }[]

    constructor(data: PODApi.Response.ParentOrder) {
      this.groupOrderId = data.groupOrderId
      this.productList = data.productList.map((product) => ({
        ...product,
        orderDatetime: new Date(product.orderDatetime),
        salesPrice: product.discountedPrice + product.additionalPrice,
      }))
    }
  }

  export type OrderItem =
    | CustomSignatureWorkbookOrderItem
    | CustomWorkbookOrderItem
    | PrintDocumentOrderItem

  export class CustomSignatureWorkbookOrderItem
    implements Omit<PODApi.Response.CustomSignatureWorkbookOrderItem, 'coverData' | 'coverNumber'>
  {
    id: number
    orderId: string
    customSignatureWorkbookId: number
    name: string

    thumbnailImageUrl: string
    thumbnailImageKey: string
    productId: ProductDetailEntity.ProductDetail['id']
    productType: Extract<ProductType, 'SIGNATURE_WORKBOOK'>
    productStatus: WorkbookEntity.CustomSignatureWorkbook['productStatus']

    quantity: number
    price: number
    discountedPrice: number
    additionalPrice?: number
    totalAmount: number
    studentNames: string[]

    signatureType: null
    // TODO: 바뀌는거 맞는지 확인 - ValueOf<typeof WorkbookDomain.SIGNATURE_TYPE> 였는데 왜인지 null이 날라옴
    pdfType: PdfType
    printType: PrintType
    coverData: NonNullable<WorkbookEntity.CustomSignatureWorkbook['coverData']>

    // TODO: 바뀌는거 맞는지 확인 -
    // grade: WorkbookEntity.SchoolWorkbook['grade']
    // semester: WorkbookEntity.SchoolWorkbook['semester']
    // revision: WorkbookEntity.SchoolWorkbook['revision']
    // schoolType: WorkbookEntity.SchoolWorkbook['schoolType']
    grade: null
    semester: null
    revision: null
    schoolType: null
    problemPageCount: ProductDetailEntity.ProductDetail['problemPageCount']
    problemAnswerPageCount: ProductDetailEntity.ProductDetail['problemAnswerPageCount']
    showPrice: CartItemEntity.CartItem['showPrice']

    constructor(
      props:
        | Entity.CustomSignatureWorkbookOrderItem
        | PODApi.Response.CustomSignatureWorkbookOrderItem,
    ) {
      this.id = props.id
      this.orderId = props.orderId
      this.name = props.name
      this.thumbnailImageUrl = props.thumbnailImageUrl
      this.productId = props.productId
      this.productType = props.productType
      this.quantity = props.quantity
      this.price = props.price
      this.discountedPrice = props.discountedPrice
      this.additionalPrice = props.additionalPrice
      this.totalAmount = props.totalAmount
      this.pdfType = props.pdfType
      this.printType = props.printType
      this.coverData = new CoverData<WorkbookEntity.CustomSignatureWorkbookCoverDataT>(
        props.coverData,
      )
      this.studentNames = props.studentNames
      this.customSignatureWorkbookId = props.customSignatureWorkbookId
      this.thumbnailImageKey = props.thumbnailImageKey
      this.revision = props.revision
      this.schoolType = props.schoolType
      this.grade = props.grade
      this.semester = props.semester
      this.signatureType = props.signatureType
      this.productStatus = props.productStatus
      this.problemPageCount = props.problemPageCount
      this.problemAnswerPageCount = props.problemAnswerPageCount
      this.showPrice = props.showPrice
    }
  }
  export class CustomWorkbookOrderItem
    implements Omit<PODApi.Response.CustomWorkbookOrderItem, 'coverData'>
  {
    id: number
    orderId: string
    customWorkbookId: WorkbookEntity.CustomWorkbook['id']
    name: string

    thumbnailImageUrl: string
    productId: number
    productType: Extract<ProductType, 'CUSTOM_WORKBOOK'>

    quantity: number
    price: number
    discountedPrice: number
    additionalPrice?: number
    totalAmount: number
    studentNames: string[]

    pdfType: PdfType
    printType: PrintType
    coverData: WorkbookEntity.CustomWorkbook['coverData'] = null
    coverNumber: number
    innerPdfUrl: string

    problemPageCount: ProductDetailEntity.ProductDetail['problemPageCount']
    problemAnswerPageCount: ProductDetailEntity.ProductDetail['problemAnswerPageCount']
    problemAnswerBwPrice: ProductDetailEntity.ProductDetail['problemAnswerBwPrice']
    problemAnswerColorPrice: ProductDetailEntity.ProductDetail['problemAnswerColorPrice']
    problemBwPrice: ProductDetailEntity.ProductDetail['problemBwPrice']
    problemColorPrice: ProductDetailEntity.ProductDetail['problemColorPrice']
    showPrice: CartItemEntity.CartItem['showPrice']

    constructor(props: Entity.CustomWorkbookOrderItem | PODApi.Response.CustomWorkbookOrderItem) {
      this.id = props.id
      this.orderId = props.orderId
      this.name = props.name
      this.thumbnailImageUrl = props.thumbnailImageUrl
      this.productId = props.productId
      this.productType = props.productType
      this.quantity = props.quantity
      this.price = props.price
      this.discountedPrice = props.discountedPrice
      this.additionalPrice = props.additionalPrice
      this.totalAmount = props.totalAmount
      this.pdfType = props.pdfType
      this.printType = props.printType
      if (props.coverData) {
        this.coverData = new CoverData<WorkbookEntity.CustomSignatureWorkbookCoverDataT>(
          props.coverData,
        )
      }

      this.studentNames = props.studentNames
      this.customWorkbookId = props.customWorkbookId
      this.coverNumber = props.coverNumber
      this.innerPdfUrl = props.innerPdfUrl
      this.problemPageCount = props.problemPageCount
      this.problemAnswerPageCount = props.problemAnswerPageCount
      this.problemAnswerBwPrice = props.problemAnswerBwPrice
      this.problemAnswerColorPrice = props.problemAnswerColorPrice
      this.problemBwPrice = props.problemBwPrice
      this.problemColorPrice = props.problemColorPrice
      this.showPrice = props.showPrice
    }
  }

  export class PrintDocumentOrderItem
    implements Omit<PODApi.Response.PrintDocumentOrderItem, 'coverData'>
  {
    id: PODApi.Response.PrintDocumentOrderItem['id']
    orderId: PODApi.Response.PrintDocumentOrderItem['orderId']
    name: PODApi.Response.PrintDocumentOrderItem['name']
    thumbnailImageUrl: PODApi.Response.PrintDocumentOrderItem['thumbnailImageUrl']
    productId: PODApi.Response.PrintDocumentOrderItem['productId']
    productType: PODApi.Response.PrintDocumentOrderItem['productType']
    quantity: PODApi.Response.PrintDocumentOrderItem['quantity']
    price: PODApi.Response.PrintDocumentOrderItem['price']
    additionalPrice?: PODApi.Response.PrintDocumentOrderItem['additionalPrice']
    totalAmount: PODApi.Response.PrintDocumentOrderItem['totalAmount']
    printType: PODApi.Response.PrintDocumentOrderItem['printType']
    coverData: CoverData<PrintDocumentCoverDataT>
    studentNames: PODApi.Response.PrintDocumentOrderItem['studentNames']
    productStatus: PODApi.Response.PrintDocumentOrderItem['productStatus']
    discountedPrice: PODApi.Response.PrintDocumentOrderItem['discountedPrice']
    printDocumentId: PODApi.Response.PrintDocumentOrderItem['printDocumentId']
    pageCount: PODApi.Response.PrintDocumentOrderItem['pageCount']
    fileKey: PODApi.Response.PrintDocumentOrderItem['fileKey']
    thumbnailImageKey: PODApi.Response.PrintDocumentOrderItem['thumbnailImageKey']
    showPrice: CartItemEntity.CartItem['showPrice']

    constructor(props: PODApi.Response.PrintDocumentOrderItem) {
      this.id = props.id
      this.orderId = props.orderId
      this.name = props.name
      this.thumbnailImageUrl = props.thumbnailImageUrl
      this.productId = props.productId
      this.productType = props.productType
      this.quantity = props.quantity
      this.price = props.price
      this.additionalPrice = props.additionalPrice
      this.totalAmount = props.totalAmount
      this.printType = props.printType
      this.studentNames = props.studentNames
      this.productStatus = props.productStatus
      this.discountedPrice = props.discountedPrice
      this.printDocumentId = props.printDocumentId
      this.coverData = new CoverData<PrintDocumentCoverDataT>(props.coverData)
      this.pageCount = props.pageCount
      this.fileKey = props.fileKey
      this.thumbnailImageKey = props.thumbnailImageKey
      this.showPrice = props.showPrice
    }
  }
}
